import { SubmissionError } from "redux-form";
import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";

import { Snackbar } from "@js/components/snackbar";
import type { AppThunkAction } from "@js/store";
import type {
  StripeWithdrawalMethod,
  TransferWiseWithdrawalMethod,
  WithdrawalFormRequirements,
} from "@js/types/withdrawals";

import type { OTPFieldValueType } from "../common/forms/fields/otp/otp-auth-modal";

import type { AddRecipientsFormData } from "./forms/add-recipients";
import * as actionTypes from "./action-types";

const checkStatusAndShowPaymentProviderErrorSnackbar = <
  Provider extends "Stripe" | "Wise",
>(
  error: AxiosError,
  provider: Provider,
) => {
  if (error.response && error.response.status >= 500) {
    Snackbar.error(`${provider} is temporarily down, please try again later.`);
  }
};

export const fetchTransferWiseCurrencies =
  (): AppThunkAction<Promise<void>> => (dispatch) =>
    new Promise((resolve) => {
      dispatch({
        type: actionTypes.FETCH_TRANSFERWISE_CURRENCIES,
      });

      return axios
        .get(`/api/transferwise_accounts/currencies/`)
        .then((response) => {
          dispatch({
            type: actionTypes.FETCH_TRANSFERWISE_CURRENCIES_SUCCESS,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch((error) => {
          checkStatusAndShowPaymentProviderErrorSnackbar(error, "Wise");

          dispatch({
            type: actionTypes.FETCH_TRANSFERWISE_CURRENCIES_FAILED,
          });
        });
    });

export const fetchCurrencyRequirementsClear = () => ({
  type: actionTypes.FETCH_WITHDRAWAL_METHODS_OTHER_REQUIREMENTS_CLEAR,
});

export const fetchCurrencyRequirements =
  (currency: string): AppThunkAction<Promise<WithdrawalFormRequirements>> =>
  (dispatch) =>
    new Promise((resolve, reject) => {
      dispatch({
        type: actionTypes.FETCH_WITHDRAWAL_METHODS_CURRENCY_REQUIREMENTS,
      });

      return axios
        .get(`/api/transferwise_accounts/requirements/?currency=${currency}`)
        .then((response: AxiosResponse) => {
          dispatch({
            type: actionTypes.FETCH_WITHDRAWAL_METHODS_CURRENCY_REQUIREMENTS_SUCCESS,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch((error: AxiosError<Record<string, unknown>>) => {
          dispatch({
            type: actionTypes.FETCH_WITHDRAWAL_METHODS_CURRENCY_REQUIREMENTS_FAILED,
          });

          if (error.response) {
            return reject(new SubmissionError(error.response.data as object));
          }

          reject(
            new SubmissionError({
              _error: "There was a problem with this request",
            }),
          );
        });
    });

export const fetchOtherRequirements =
  (
    data: Record<string, unknown>,
    abortController?: AbortController,
  ): AppThunkAction<Promise<WithdrawalFormRequirements>> =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      dispatch({
        type: actionTypes.FETCH_WITHDRAWAL_METHODS_OTHER_REQUIREMENTS,
      });

      return axios
        .post(`/api/transferwise_accounts/requirements/`, data, {
          signal: abortController?.signal,
        })
        .then((response: AxiosResponse<WithdrawalFormRequirements>) => {
          dispatch({
            type: actionTypes.FETCH_WITHDRAWAL_METHODS_OTHER_REQUIREMENTS_SUCCESS,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch((error: AxiosError<Record<string, unknown>>) => {
          if (axios.isCancel(error)) {
            return reject({ isCancel: true });
          }

          if (error.response) {
            checkStatusAndShowPaymentProviderErrorSnackbar(error, "Wise");
            return reject(error.response.data);
          }

          reject({ _error: "There was a problem with this request" });
        });
    });
  };

export type CreateTransferWiseWithdrawalMethod =
  | (Omit<TransferWiseWithdrawalMethod, "id"> & { code?: string })
  | (Omit<StripeWithdrawalMethod, "id"> & { code?: string });

export const createTransferWiseWithdrawalMethod =
  (
    withdrawalMethod: CreateTransferWiseWithdrawalMethod,
  ): AppThunkAction<Promise<void>> =>
  (dispatch) =>
    new Promise((resolve, reject) => {
      dispatch({
        type: actionTypes.CREATE_WITHDRAWAL_METHOD,
      });

      return axios
        .post(`/api/transferwise_accounts/`, withdrawalMethod)
        .then((response) => {
          dispatch({
            type: actionTypes.CREATE_WITHDRAWAL_METHOD_SUCCESS,
            payload: response.data,
          });

          Snackbar.info(
            "Please check your inbox to confirm new bank account information.",
          );
          resolve(response.data);
        })
        .catch((error) => {
          const { data } = error.response;

          checkStatusAndShowPaymentProviderErrorSnackbar(error, "Wise");

          dispatch({
            type: actionTypes.CREATE_WITHDRAWAL_METHOD_FAILED,
          });

          reject(new SubmissionError(data));
        });
    });

export const createStripeWithdrawalMethod =
  (): AppThunkAction<Promise<void>> => (dispatch) =>
    new Promise((resolve, reject) => {
      dispatch({
        type: actionTypes.CREATE_WITHDRAWAL_METHOD,
      });

      return axios
        .post(`/api/stripe_accounts/`)
        .then((response) => {
          dispatch({
            type: actionTypes.CREATE_WITHDRAWAL_METHOD_SUCCESS,
            payload: response.data,
          });

          Snackbar.info(
            "Please check your inbox to confirm new withdrawal method.",
          );
          resolve(response.data);
        })
        .catch((error) => {
          const { data } = error.response;
          checkStatusAndShowPaymentProviderErrorSnackbar(error, "Stripe");
          dispatch({
            type: actionTypes.CREATE_WITHDRAWAL_METHOD_FAILED,
            payload: data,
          });
          reject(new SubmissionError(data));
        });
    });

export const addStripeMethodRecipients =
  (companyNodes: AddRecipientsFormData): AppThunkAction<Promise<void>> =>
  (dispatch) => {
    dispatch({
      type: actionTypes.ADD_STRIPE_RECIPIENTS,
    });

    return axios
      .post(`/api/stripe_recipients/`, companyNodes)
      .then((response) => {
        dispatch({
          type: actionTypes.ADD_STRIPE_RECIPIENTS_SUCCESS,
          payload: response.data,
        });

        Snackbar.success("Company node added successfully.");
      })
      .catch((error) => {
        dispatch({
          type: actionTypes.ADD_STRIPE_RECIPIENTS_FAILED,
        });

        throw error;
      });
  };

export const deleteRecipient = (
  companyNodeId: number,
  twoFa: OTPFieldValueType,
) =>
  axios
    .delete(`/api/stripe_recipients/${companyNodeId}`, {
      data: twoFa,
    })
    .then(() => {
      Snackbar.success("Company node has been successfully removed.");
    });

export const fetchFreelancerCompanyNodes =
  (): AppThunkAction<Promise<void>> => (dispatch) =>
    new Promise((resolve) => {
      dispatch({
        type: actionTypes.FETCH_COMPANY_NODES,
      });

      return axios
        .get(`/api/freelancer_company_nodes/`)
        .then((response) => {
          dispatch({
            type: actionTypes.FETCH_COMPANY_NODES_SUCCESS,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch(() =>
          dispatch({
            type: actionTypes.FETCH_COMPANY_NODES_FAILED,
          }),
        );
    });

export const deleteWithdrawalMethod = (
  withdrawalMethodId: number,
  twoFa: OTPFieldValueType,
) => {
  return axios.delete(`/api/withdrawal_methods/${withdrawalMethodId}/`, {
    data: {
      code: twoFa.code,
      is_backup_code: twoFa.is_backup_code,
      key: twoFa.key,
    },
  });
};

export const setAsDefaultWithdrawalMethod = (
  withdrawalMethodId: number,
  twoFa: OTPFieldValueType,
) =>
  axios
    .post(
      `/api/withdrawal_methods/${withdrawalMethodId}/prepare_to_set_as_default/`,
      {
        code: twoFa.code,
        is_backup_code: twoFa.is_backup_code,
        key: twoFa.key,
      },
    )
    .then(() =>
      Snackbar.success(
        "An email with a confirmation link has been sent to your email address.",
      ),
    );

export const resendConfirmAddWithdrawalMethod = () =>
  new Promise(() =>
    axios
      .post(`/api/stripe_accounts/resend_confirmation_email/`)
      .then(() =>
        Snackbar.success(
          "An email with a confirmation link has been sent to your email address.",
        ),
      )
      .catch((error) =>
        Snackbar.error(
          error.response.data || "Failed to resend confirmation email",
        ),
      ),
  );

export const addRecipientOnboardingLink = ({
  id,
  link,
}: {
  id: number;
  link: string;
}) => ({
  type: actionTypes.ADD_STRIPE_RECIPIENTS_ONBOARDING_LINK,
  payload: {
    id,
    link,
  },
});

export const removeRecipientOnboardingLink = (id: number) => ({
  type: actionTypes.REMOVE_STRIPE_RECIPIENTS_ONBOARDING_LINK,
  payload: id,
});
